import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Grid, Loader } from 'semantic-ui-react';
import { logError } from '../../../rollbar';

import { userHasPermission } from '../../../api/firebase/account';
import { CSV, DOC, DOCX, JPEG, PDF, PNG, TXT, XLS, XLSX } from '../../../helpers/mimeTypes';
import { formatFullName } from '../../../helpers/utils';
import { useOrganization } from '../../../hooks/useOrganizations';
import { usePermissions, useUser } from '../../../hooks/useUser';

import { organizationUpdateStudent, studentUpdated } from '../../../redux/actions/studentsActions';
import {
  recoverStudentInformation,
  sendStudentInformationSupportRequest,
  type StudentInformationIssueType,
  type StudentInformationRecoveryAction,
  type StudentInformationRecoveryResult,
} from '../../studentInformationSupportAPI';
import { fetchBillingDetails, saveStudent } from '../../studentsAPI';
import { useSelectedStudent } from '../../studentsHooks';
import {
  archiveStudent,
  canArchiveStudent,
  computeEnforceAutomaticPayment,
  isStudentArchived,
  unarchiveStudent,
} from '../../studentsUtils';

import {
  AddCircleIcon,
  Button,
  ContactsIcon,
  Dialog,
  EditIcon,
  Switch,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import DocumentUploader from '../../../Components/Upload/DocumentUploader';

import StudentArchiveConfirmationModal from '../StudentArchiveConfirmationModal';
import StudentDocumentList from '../StudentDocumentList';
import StudentInvoicesTable from '../StudentInvoicesTable';
import StudentProgramsForm from '../StudentProgramsForm';
import { SendEnrollmentInvitationButton } from '../enrollment/SendEnrollmentInvitationButton';
import StudentEnrollment from '../enrollment/StudentEnrollment';
import StudentEnrollmentForm from '../enrollment/StudentEnrollmentForm';
import { FamilyList } from '../family';
import { FamilyEditContactModal } from '../family/FamilyEditContactModal';
import type { StudentInformationRecoveryState } from './StudentInformationRecovery';
import type { PersonalInformationLoadState } from './StudentPersonalInformationForm';
import { StudentPersonalInformationForm } from './StudentPersonalInformationForm';
import { StudentProgramsProviderApi } from './StudentProgramsProviderApi';

type BillingDetails = {
  hasPaymentMethod: boolean;
  canChargeAutomatically: boolean;
};

const sanitizedErrorName = (error: unknown) => (error instanceof Error ? error.name : 'UnknownError');

type StudentDetailHeaderProps = {
  children: ReactNode | ReactNode[];
  'data-testid'?: string;
};

function StudentDetailHeader({ children, 'data-testid': dataTestId }: StudentDetailHeaderProps) {
  return (
    <h2 className="mb-6 flex items-center justify-between text-xl font-bold" data-testid={dataTestId}>
      {children}
    </h2>
  );
}

export default function StudentDetailProviderApi() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const organization = useOrganization();
  const selectedStudent = useSelectedStudent();

  const [isStudentEnrollmentFormOpen, setIsStudentEnrollmentFormOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  const [isStudentProgramsFormOpen, setIsStudentProgramsFormOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [recoveryState, setRecoveryState] = useState<StudentInformationRecoveryState>('idle');
  const [recoveryAttempt, setRecoveryAttempt] = useState(0);
  const [isRecoveryUnavailable, setIsRecoveryUnavailable] = useState(false);
  const selectedStudentIdRef = useRef(selectedStudent.id);
  const verificationRetryRef = useRef(0);
  const verificationRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const user = useUser();
  const permissions = usePermissions();
  const canRoleListBilling = userHasPermission('can_list_billing');
  const canRoleEditStudent = userHasPermission('can_edit_student');
  const canRoleEditStudentContacts = userHasPermission('can_edit_student_contacts');
  const { isStudentInformationRecoveryEnabled = false } = useFlags();
  const canEditStudentInOrganization =
    user.claims?.moxitAdmin === true ||
    (user.enabled === true &&
      Array.isArray(user.staffType) &&
      Array.isArray(permissions.can_edit_student?.roles) &&
      user.staffType.some((role) => permissions.can_edit_student.roles.includes(role)));
  const canRecoverStudentInformation =
    isStudentInformationRecoveryEnabled && !isRecoveryUnavailable && !user.isParent && canEditStudentInOrganization;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    selectedStudentIdRef.current = selectedStudent.id;
    if (verificationRetryTimerRef.current) clearTimeout(verificationRetryTimerRef.current);
    if (recoveryStatusTimerRef.current) clearTimeout(recoveryStatusTimerRef.current);
    verificationRetryRef.current = 0;
    setRecoveryState('idle');
    setRecoveryAttempt(0);
    setIsRecoveryUnavailable(false);
    return () => {
      if (verificationRetryTimerRef.current) clearTimeout(verificationRetryTimerRef.current);
      if (recoveryStatusTimerRef.current) clearTimeout(recoveryStatusTimerRef.current);
    };
  }, [selectedStudent.id]);

  const handleRecoveryResult = useCallback((result: StudentInformationRecoveryResult) => {
    if (result.status === 'succeeded') {
      setRecoveryState('verifying');
      setRecoveryAttempt((attempt) => attempt + 1);
    } else if (result.status === 'unavailable') {
      setIsRecoveryUnavailable(true);
      setRecoveryState('idle');
    } else if (result.status === 'failed') {
      setRecoveryState('error');
    } else if (result.status === 'in_progress') {
      setRecoveryState('restoring');
    }
  }, []);

  const checkRecoveryStatus = useCallback(async () => {
    const studentId = selectedStudent.id;
    try {
      const result = await recoverStudentInformation({
        organizationId: organization.id,
        studentId,
        action: 'status',
      });
      if (!isMountedRef.current || selectedStudentIdRef.current !== studentId) return;
      handleRecoveryResult(result);
      if (result.status === 'in_progress') {
        recoveryStatusTimerRef.current = setTimeout(() => void checkRecoveryStatus(), 2_000);
      }
    } catch (error) {
      logError(
        'Error checking student information recovery status.',
        new Error('Student recovery status check failed'),
        {
          organizationFirebaseId: organization.id,
          studentFirebaseId: studentId,
          errorName: sanitizedErrorName(error),
        }
      );
      if (isMountedRef.current && selectedStudentIdRef.current === studentId) setRecoveryState('error');
    }
  }, [handleRecoveryResult, organization.id, selectedStudent.id]);

  const restoreStudentInformation = useCallback(
    async (action: StudentInformationRecoveryAction = 'start') => {
      const studentId = selectedStudent.id;
      if (verificationRetryTimerRef.current) clearTimeout(verificationRetryTimerRef.current);
      if (recoveryStatusTimerRef.current) clearTimeout(recoveryStatusTimerRef.current);
      verificationRetryRef.current = 0;
      setRecoveryState('restoring');

      try {
        const result = await recoverStudentInformation({
          organizationId: organization.id,
          studentId,
          action,
          attemptId: crypto.randomUUID(),
        });
        if (!isMountedRef.current || selectedStudentIdRef.current !== studentId) return;
        handleRecoveryResult(result);
        if (result.status === 'in_progress') {
          recoveryStatusTimerRef.current = setTimeout(() => void checkRecoveryStatus(), 2_000);
        }
      } catch (error) {
        logError('Error trying to restore student information.', new Error('Student recovery request failed'), {
          organizationFirebaseId: organization.id,
          studentFirebaseId: studentId,
          errorName: sanitizedErrorName(error),
        });
        if (!isMountedRef.current || selectedStudentIdRef.current !== studentId) return;
        setRecoveryState('error');
      }
    },
    [checkRecoveryStatus, handleRecoveryResult, organization.id, selectedStudent.id]
  );

  const onPersonalInformationLoadStateChange = useCallback(
    (loadState: PersonalInformationLoadState) => {
      if (canRecoverStudentInformation && loadState === 'missing' && recoveryState === 'idle') {
        void restoreStudentInformation('start');
        return;
      }

      if (recoveryState !== 'verifying') return;
      if (loadState === 'loaded') {
        if (verificationRetryTimerRef.current) clearTimeout(verificationRetryTimerRef.current);
        setRecoveryState('idle');
        return;
      }
      if (loadState === 'error') {
        setRecoveryState('error');
        return;
      }
      if (loadState === 'missing' && verificationRetryRef.current === 0) {
        const studentId = selectedStudentIdRef.current;
        verificationRetryRef.current = 1;
        verificationRetryTimerRef.current = setTimeout(() => {
          if (selectedStudentIdRef.current !== studentId) return;
          verificationRetryRef.current = 2;
          setRecoveryAttempt((attempt) => attempt + 1);
        }, 1000);
      } else if (loadState === 'missing' && verificationRetryRef.current === 2) {
        setRecoveryState('error');
      }
    },
    [canRecoverStudentInformation, recoveryState, restoreStudentInformation]
  );

  const submitStudentInformationSupportRequest = useCallback(
    (issueType: StudentInformationIssueType, requestId: string) =>
      sendStudentInformationSupportRequest({
        organizationId: organization.id,
        studentId: selectedStudent.id,
        issueType,
        requestId,
      }),
    [organization.id, selectedStudent.id]
  );

  const allowedMimeTypes = {
    PDF,
    PNG,
    JPEG,
    DOC,
    DOCX,
    XLS,
    XLSX,
    CSV,
    TXT,
  };

  const allowedFileTypes = Object.values(allowedMimeTypes);
  const allowedFileTypeNames = Object.keys(allowedMimeTypes);

  const allowedFileTypeNamesStart = allowedFileTypeNames.slice(0, -1).join(', ');
  const allowedFileTypeNamesEnd = allowedFileTypeNames.slice(-1);

  const maxFileSize = '30MB';

  const processUploadedDocuments = (uploads) => {
    if (!uploads?.length) return;

    const { uid, email } = user;

    const documentUploadPath = `organizations/${organization.id}/students/${selectedStudent.id}/documents`;

    const documents = {
      ...selectedStudent.documents,
    };

    uploads.forEach((upload) => {
      const name = upload.name ? upload.name.split('.')[0] : 'Unknown';

      documents[upload.meta.refId] = {
        name,
        documentId: upload.meta.refId,
        path: documentUploadPath,
        type: upload.type,
        size: upload.size,
        extension: upload.extension,
        progress: upload.progress,
        meta: upload.meta,
        downloadUrl: upload.downloadUrl,
        uploadedBy: { uid, email, name: formatFullName(user, true) },
        uploadedDate: Date.now(),
        tags: [],
      };
    });

    dispatch(
      organizationUpdateStudent(organization.id, {
        ...selectedStudent,
        documents,
      })
    );
  };
  const onSaveStudent = async (newStudent) => {
    try {
      await saveStudent(organization.id, newStudent);
      dispatch(studentUpdated());
      setIsArchiveModalOpen(false);
    } catch (error: any) {
      logError('Error trying to save student.', error);
      throw new Error(error);
    }
  };
  const onArchiveStudent = async () => {
    try {
      const newStudent = archiveStudent(selectedStudent);
      await onSaveStudent(newStudent);
    } catch (error: any) {
      logError('Error trying to archive student.', error);
      throw new Error(error);
    }
  };
  const onRestoreStudent = async () => {
    try {
      const newStudent = unarchiveStudent(selectedStudent);
      await onSaveStudent(newStudent);
    } catch (e: any) {
      logError('Error trying to unarchive student.', e);
      throw new Error(e);
    }
  };
  const onEnforceAutomaticPaymentChange = (e, { checked }: { checked?: boolean }) => {
    if (!selectedStudent?.id || !organization?.id || user.isParent) return;

    if (!selectedStudent.automaticPayment) {
      selectedStudent.automaticPayment = {};
    }
    selectedStudent.automaticPayment.enforce = checked;
    selectedStudent.automaticPayment.modifiedDate = Date.now();

    dispatch(organizationUpdateStudent(organization.id, selectedStudent));
  };

  useEffect(() => {
    const fetchData = async () => {
      const billingDetails = {
        hasPaymentMethod: false,
        canChargeAutomatically: false,
      };

      try {
        Object.assign(billingDetails, await fetchBillingDetails(organization.id, selectedStudent.id));
      } catch (e) {
        console.error('Error trying to get billing details for responsible parent.', e);
      }

      setBillingDetails(billingDetails);
    };

    if (!user.isParent && canRoleListBilling && selectedStudent.id) {
      fetchData();
    }
  }, [selectedStudent.id, organization.id, user.isParent, canRoleListBilling]);

  const renderStudentEnrollmentForm = useCallback(() => {
    return <StudentEnrollmentForm student={selectedStudent} onClose={() => setIsStudentEnrollmentFormOpen(false)} />;
  }, [selectedStudent]);

  const renderStudentProgramsForm = useCallback(() => {
    return <StudentProgramsForm student={selectedStudent} onClose={() => setIsStudentProgramsFormOpen(false)} />;
  }, [selectedStudent]);

  return (
    <>
      {/* TODO: this is a temporary rollback to the old contact modal */}
      <FamilyEditContactModal
        student={selectedStudent}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
      <StudentArchiveConfirmationModal
        isOpen={isArchiveModalOpen}
        onYes={onArchiveStudent}
        onNo={() => setIsArchiveModalOpen(false)}
      />
      <div className="flex flex-col gap-5">
        <StudentPersonalInformationForm
          canRecover={canRecoverStudentInformation}
          recoveryState={recoveryState}
          onRetryRecovery={() => void restoreStudentInformation('retry')}
          onSendSupportRequest={canRecoverStudentInformation ? submitStudentInformationSupportRequest : undefined}
          onLoadStateChange={onPersonalInformationLoadStateChange}
          refreshToken={recoveryAttempt}
        />
        <StudentEnrollmentSegment />
        <StudentProgramsSegment />
        <OldContactList />
        {/*<FamilyContactsForm studentFirebaseId={selectedStudent.id} canEdit={canRoleEditStudentContacts} />*/}
        {/* temporarily hiding this view from parents until invoice data issue is resolved */}
        {!user.isParent && canRoleListBilling && <AllInvoicesSegment />}
        <StudentDocumentListSegment />
        <StudentArchiveActionsSegment />
      </div>
    </>
  );

  function StudentEnrollmentSegment() {
    return (
      <div>
        <StudentDetailHeader data-testid="enrollment-title">
          {t('Enrollment')}
          {canRoleEditStudent && (
            <div className="flex items-center gap-3">
              <SendEnrollmentInvitationButton student={selectedStudent} canEdit={canRoleEditStudent} />
              <Button
                extraClasses="ring-0 ring-offset-0 shadow-none"
                onClick={(e) => {
                  e.preventDefault();
                  setIsStudentEnrollmentFormOpen(true);
                }}
                data-testid="enrollment-edit-btn"
              >
                <EditIcon className="mr-2 size-4" />
                {t('common.edit')}
              </Button>
            </div>
          )}
        </StudentDetailHeader>

        <StudentEnrollment student={selectedStudent} />

        <Dialog
          isOpen={isStudentEnrollmentFormOpen}
          onClose={() => setIsStudentEnrollmentFormOpen(false)}
          data-testid="student-enrollment-form-modal"
          hasCloseButton={true}
          fullScreenOnMobile={true}
        >
          <Dialog.Panel>
            <Dialog.Title>{t('Enrollment')}</Dialog.Title>
            <Dialog.Description>{renderStudentEnrollmentForm()}</Dialog.Description>
          </Dialog.Panel>
        </Dialog>
      </div>
    );
  }

  function StudentProgramsSegment() {
    return (
      <div className="py-4">
        <StudentDetailHeader data-testid="programs-title">
          {t('students.details.programs.heading')}
          {canRoleEditStudent && (
            <Button
              extraClasses="ring-0 ring-offset-0 shadow-none"
              onClick={(e) => {
                e.preventDefault();
                setIsStudentProgramsFormOpen(true);
              }}
              data-testid="programs-edit-btn"
            >
              <EditIcon className="mr-2 size-4" />
              {t('common.edit')}
            </Button>
          )}
        </StudentDetailHeader>

        <StudentProgramsProviderApi student={selectedStudent} />

        <Dialog
          isOpen={isStudentProgramsFormOpen}
          onClose={() => setIsStudentProgramsFormOpen(false)}
          data-testid="student-programs-form-modal"
        >
          <Dialog.Panel>
            <Dialog.Title>{t('students.details.programs.heading')}</Dialog.Title>
            <Dialog.Description>{renderStudentProgramsForm()}</Dialog.Description>
          </Dialog.Panel>
        </Dialog>
      </div>
    );
  }

  function AllInvoicesSegment() {
    return (
      <div className="py-4">
        <StudentDetailHeader data-testid="invoices-title">
          {t('All Invoices')}
          <Switch
            id="enforceAutomaticPayment"
            name="enforceAutomaticPayment"
            label={
              <label>
                {t('studentDetailsEnforce')} <strong>{t('studentDetailsAutomaticPayment')}</strong>
              </label>
            }
            disabled={user.isParent}
            checked={computeEnforceAutomaticPayment(selectedStudent, organization)}
            onChange={(e) => onEnforceAutomaticPaymentChange(e, { checked: e.target.checked })}
            wrapperClasses="flex-row-reverse"
            size={WidgetSizeEnum.SMALL}
            data-testid="auto-payment"
          />
        </StudentDetailHeader>

        <div className="flex justify-between">
          <div className="inline-block">
            <strong>{t('studentDetailsResponsibleParentOnAutomaticPayment')}: </strong>
            {billingDetails ? (
              t(
                billingDetails.canChargeAutomatically
                  ? 'studentDetailsResponsibleParentOnAutomaticPaymentYes'
                  : 'studentDetailsResponsibleParentOnAutomaticPaymentNo'
              )
            ) : (
              <Loader size="mini" active inline />
            )}
          </div>
          <div className="inline-block">
            <i className="credit card icon outline"></i>
            {billingDetails ? (
              t(
                billingDetails.hasPaymentMethod
                  ? 'studentDetailsPaymentMethodAdded'
                  : 'studentDetailsNoPaymentMethodAdded'
              )
            ) : (
              <Loader size="mini" active inline></Loader>
            )}
          </div>
        </div>
        <StudentInvoicesTable />
      </div>
    );
  }

  function StudentDocumentListSegment() {
    return (
      <div className="py-4">
        <StudentDetailHeader data-testid="files-title">
          {t('Files')}
          {canRoleEditStudent && (
            <DocumentUploader
              uploadPath={`organizations/${organization.id}/students/${selectedStudent.id}/documents`}
              title={t('Uploading Student Documents')}
              allowedFileTypes={allowedFileTypes}
              onUploaded={processUploadedDocuments}
              note={t(
                'Only {{allowedFileTypeNamesStart}}, or {{allowedFileTypeNamesEnd}} files up to {{maxFileSize}} are allowed.',
                {
                  allowedFileTypeNamesStart,
                  allowedFileTypeNamesEnd,
                  maxFileSize,
                }
              )}
              data-testid="upload-doc"
            />
          )}
        </StudentDetailHeader>
        <StudentDocumentList />
      </div>
    );
  }

  function StudentArchiveActionsSegment() {
    return (
      <div className="py-4">
        {canRoleEditStudent && !isStudentArchived(selectedStudent) && canArchiveStudent(selectedStudent) && (
          <div className="flex items-center justify-between">
            <span>{t('students.archiveStudentModal.hideRemoveStudentFromView')}</span>
            <Button
              extraClasses="ring-0 ring-offset-0 shadow-none bg-red-800 hover:bg-red-700 text-neutral-50"
              onClick={(e) => {
                e.preventDefault();
                setIsArchiveModalOpen(true);
              }}
              data-testid="student-archive-btn"
            >
              {t('students.archiveStudentModal.archiveStudent')}
            </Button>
          </div>
        )}
        {canRoleEditStudent && isStudentArchived(selectedStudent) && (
          <div className="flex items-center justify-between">
            <span>{t('students.archiveStudentModal.unarchiveStudent')}</span>
            <Button
              primary
              extraClasses="ring-0 ring-offset-0 shadow-none"
              onClick={(e) => {
                e.preventDefault();
                onRestoreStudent();
              }}
              data-testid="student-unarchive-btn"
            >
              {t('students.archiveStudentModal.restoreStudent')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  function OldContactList() {
    return (
      <Grid stackable columns="equal">
        <Grid.Column>
          <h2 data-testid="family-contact-title" className={'mb-4 flex items-center justify-between'}>
            <span className="text-xl">
              <ContactsIcon className="size-6 text-gray-800" /> {t('familyAndContactsLabel')}
            </span>
            {canRoleEditStudentContacts && (
              <Button
                data-testid="add-contact-btn"
                onClick={() => setIsAddModalOpen(true)}
                size={WidgetSizeEnum.SMALL}
                primary
                postIcon={<AddCircleIcon className="size-5" />}
              >
                {t('common.addContact')}
              </Button>
            )}
          </h2>
          <FamilyList />
        </Grid.Column>
      </Grid>
    );
  }
}
